import {useIsBotModeEnabled} from '@aboutyou/data-fetcher';
import {BasketToken} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/basket_token.types';
import React, {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {useAnySession} from '../userStore/useSession';

const BasketTokenContext = createContext<{
  updateBasketToken: (token: BasketToken | undefined) => void;
  setBasketTokenFetchFailed: () => void;
  token: BasketToken | null;
  /* Cache key token is string representation of basket token
   * Its used for verifying caches and breaking if something in basket changed
   * Because its a string it makes it easier to compare, see if there is some difference and also easier to debug
   */
  basketTokenHash: string | undefined;
  /**
   * use `waitForBasketTokenOnClientRequest` to `disable` the requests which need the basket token to be ready on the client side.
   * NOTE: using this property does not disable the request on the server side.
   */
  waitForBasketTokenOnClientRequest: boolean;
}>(
  new Proxy(
    {
      token: null,
      updateBasketToken: () => null,
      setBasketTokenFetchFailed: () => null,
      basketTokenHash: undefined,
      waitForBasketTokenOnClientRequest: false,
    },
    {
      get() {
        throw new Error(
          'Cannot find value for BasketTokenContext. Did you add a BasketTokenProvider in one of the parent components.',
        );
      },
    },
  ),
);

const useBasketToken = () => useContext(BasketTokenContext);

const BasketTokenProvider: FC<PropsWithChildren> = ({children}) => {
  const botMode = useIsBotModeEnabled();
  const session = useAnySession();
  const [savedToken, setToken] = useState<BasketToken | null>(null);
  const [tokenFetchFailed, setTokenFetchFailed] = useState(false);

  const updateBasketToken = useCallback((newValue: BasketToken | undefined) => {
    if (!newValue) {
      return;
    }

    setToken(newValue);
  }, []);

  const setBasketTokenFetchFailed = () => setTokenFetchFailed(true);

  /**
   * Basket Token usage hints:
   * - Basket Token we use for the requests depends on whether the request is made by a bot or not.
   * - Some requests which needs BasketToken in the request and happen to refetch on the client, depends on whether the BasketToken is available or not!
   *  - On the server the Basket Token is not available by default there we send null value to the BE
   *  - But the refetching of same request on the client depends on the BasketToken
   *
   * Case Server:
   * - no basket token in the request
   * - requests are not disabled by default - `waitForBasketTokenOnClientRequest` false by default
   *
   * Case Browser:
   * - Normal users:
   *   - some requests are only enabled for refetching if the basket token is available - use `waitForBasketTokenOnClientRequest` for disabling those requests until basket token is ready
   *   - some requests doesn't have to wait for the basket token to be available - simply use `basketToken` in the request
   * - Bots:
   *   - all requests use no basket token
   *   - all requests can refetch on the client without any basket token
   */
  const memoValue = useMemo(() => {
    if (process.browser) {
      // bot's defaults in browser => no basket tokens should be used, but requests should not be blocked
      if (botMode) {
        return {
          token: null,
          updateBasketToken: () => null,
          setBasketTokenFetchFailed,
          basketTokenHash: undefined,
          waitForBasketTokenOnClientRequest: false,
        };
      }

      // normal users defaults in browser
      return {
        updateBasketToken,
        setBasketTokenFetchFailed,
        token: session.type.$case === 'noSession' ? null : savedToken,
        basketTokenHash: savedToken?.hash.toString(),
        waitForBasketTokenOnClientRequest: !savedToken && !tokenFetchFailed,
      };
    }

    // bot's server defaults
    if (botMode) {
      return {
        token: null,
        updateBasketToken: () => null,
        setBasketTokenFetchFailed,
        basketTokenHash: undefined,
        waitForBasketTokenOnClientRequest: false,
      };
    }
    // normal users server defaults
    return {
      updateBasketToken,
      setBasketTokenFetchFailed,
      token: session.type.$case === 'noSession' ? null : savedToken,
      basketTokenHash: savedToken?.hash.toString(),
      waitForBasketTokenOnClientRequest: false,
    };
  }, [botMode, updateBasketToken, session, savedToken, tokenFetchFailed]);

  return (
    <BasketTokenContext.Provider value={memoValue}>
      {children}
    </BasketTokenContext.Provider>
  );
};

export {BasketTokenProvider, useBasketToken};
